/**
 * Password Encryption Utility
 * Provides secure client-side encryption/decryption for password storage
 * Uses AES-GCM with device-specific key derivation
 */

// Generate a device-specific encryption key based on browser characteristics
const generateDeviceKey = async () => {
  const deviceInfo = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    new Date().getTimezoneOffset().toString(),
    navigator.hardwareConcurrency || '4',
    // Add a stable identifier - could be improved with more sophisticated fingerprinting
    'promptus-pwa-key-v1'
  ].join('|');

  // Create a hash of the device information
  const encoder = new TextEncoder();
  const data = encoder.encode(deviceInfo);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Import the hash as a key for AES-GCM
  return await crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
};

// Encrypt a password string
export const encryptPassword = async (password) => {
  try {
    if (!password || typeof password !== 'string') {
      throw new Error('Invalid password provided for encryption');
    }

    const key = await generateDeviceKey();
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    
    // Generate a random IV for each encryption
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      data
    );

    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    // Convert to base64 for storage
    const base64 = btoa(String.fromCharCode(...combined));
    
    return {
      encrypted: base64,
      version: 'v1' // For future compatibility
    };
  } catch (error) {
    console.error('Password encryption failed:', error);
    throw new Error('Failed to encrypt password');
  }
};

// Decrypt a password string
export const decryptPassword = async (encryptedData) => {
  try {
    if (!encryptedData) {
      throw new Error('No encrypted data provided');
    }

    // Handle both old plain text passwords and new encrypted format
    if (typeof encryptedData === 'string') {
      // This is likely a plain text password from before encryption was implemented
      return encryptedData;
    }

    if (!encryptedData.encrypted || !encryptedData.version) {
      throw new Error('Invalid encrypted data format');
    }

    const key = await generateDeviceKey();
    
    // Convert from base64
    const combined = new Uint8Array(
      atob(encryptedData.encrypted)
        .split('')
        .map(char => char.charCodeAt(0))
    );
    
    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encrypted
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error('Password decryption failed:', error);
    throw new Error('Failed to decrypt password');
  }
};

// Store encrypted password in localStorage
export const storeEncryptedPassword = async (password) => {
  try {
    const encrypted = await encryptPassword(password);
    localStorage.setItem('password', JSON.stringify(encrypted));
    return true;
  } catch (error) {
    console.error('Failed to store encrypted password:', error);
    return false;
  }
};

// Retrieve and decrypt password from localStorage
export const retrieveDecryptedPassword = async () => {
  try {
    const stored = localStorage.getItem('password');
    if (!stored) {
      return null;
    }

    let encryptedData;
    try {
      encryptedData = JSON.parse(stored);
    } catch (parseError) {
      // If JSON parsing fails, this might be a plain text password from before encryption
      console.warn('Found non-JSON password in storage, treating as plain text');
      return stored.replace(/^"|"$/g, ''); // Remove quotes if they exist
    }

    return await decryptPassword(encryptedData);
  } catch (error) {
    console.error('Failed to retrieve decrypted password:', error);
    // Clear the corrupted password data
    localStorage.removeItem('password');
    return null;
  }
};

// Migrate existing plain text password to encrypted format
export const migrateExistingPassword = async () => {
  try {
    const stored = localStorage.getItem('password');
    if (!stored) {
      return false;
    }

    let password;
    try {
      const parsed = JSON.parse(stored);
      // If parsing succeeds and it's already encrypted format, no migration needed
      if (parsed && parsed.encrypted && parsed.version) {
        return false;
      }
      // If it's a JSON string but not encrypted format, it's a plain text password
      password = typeof parsed === 'string' ? parsed : stored;
    } catch (parseError) {
      // If parsing fails, it's likely plain text
      password = stored;
    }

    // Store the password in encrypted format
    const success = await storeEncryptedPassword(password);
    if (success) {
      console.log('Successfully migrated password to encrypted format');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Password migration failed:', error);
    return false;
  }
};

// Check if Web Crypto API is supported
export const isCryptoSupported = () => {
  return !!(window.crypto && window.crypto.subtle && window.crypto.getRandomValues);
};

// Fallback for environments without crypto support
export const handlePasswordWithFallback = async (password, isStoring = true) => {
  if (!isCryptoSupported()) {
    console.warn('Web Crypto API not supported, falling back to plain text storage');
    if (isStoring) {
      localStorage.setItem('password', JSON.stringify(password));
      return true;
    } else {
      const stored = localStorage.getItem('password');
      return stored ? JSON.parse(stored) : null;
    }
  }

  if (isStoring) {
    return await storeEncryptedPassword(password);
  } else {
    return await retrieveDecryptedPassword();
  }
};